import {mapRunningTimeDto} from '@domain/shared'
import {getEventStoppageType} from '@domain/shifthandover'
import {
  RunningTimeDto,
  RunningTime,
  EquipmentData,
  RunningTimeStoppage,
  DateRange
} from '@hconnect/common/types'
import {getSystemTimezone} from '@hconnect/common/utils'
import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {alpha, Box, SxProps, Theme} from '@mui/material'
import React, {Fragment, ReactNode, RefObject, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import useResizeObserver from 'use-resize-observer'

import {RUNNING_TIMES_WIDGET_NAMESPACE} from '../../consts'
import {useStackedBarDragRange} from '../../hooks'
import type {RunningTimesStoppageCodesByType, OnDragEvent} from '../../types'
import {
  getBarPercentage,
  getBarPercentages,
  getRunningTimeStoppageLabelProps,
  getBarForChartProps,
  getStoppageDate
} from '../../utils'
import {RunningTimeStoppageLabel} from '../RunningTimeStoppageLabel'

import {BarForChart} from './BarForChart'
import {ResizeHandle} from './ResizeHandle'

interface Props {
  timeRange: DateRange
  onResize?: (data: RunningTime[], width?: number) => void
  timezone?: string
  data: RunningTime[]
  sx?: SxProps<Theme>
  aboveBarComponent?: (rt: RunningTimeDto) => ReactNode
  handleStoppageClick?: (rt: RunningTimeDto, stoppage?: RunningTimeStoppage) => void | Promise<void>
  handleRunningClick?: (rt: RunningTimeDto) => void | Promise<void>
  equipment?: EquipmentData
  stoppageCodesByType: RunningTimesStoppageCodesByType
  onDragEnd?: OnDragEvent
  onMove?: OnDragEvent
  isEditMode?: boolean
  showDraggableArea?: boolean
  resizeHandleTooltipDefaultIndex?: number
  getResizeHandleTooltip?: (rt: RunningTime, nextRt?: RunningTime) => ReactNode
  resizeHandlePlacement?: 'top' | 'bottom'
  resizeHandleContainerRef?: RefObject<HTMLDivElement>
  useTechnicalDates?: boolean
  isLoading?: boolean
  renderTooltipFooter?: (rt: RunningTime) => ReactNode
  highlightedRunningTimeId?: number
}

export const StackedBarChart: React.FC<Props> = ({
  timeRange,
  timezone: contextTimezone,
  data,
  sx,
  handleStoppageClick,
  handleRunningClick,
  aboveBarComponent,
  equipment,
  stoppageCodesByType,
  isEditMode,
  onDragEnd,
  onMove,
  showDraggableArea,
  onResize,
  resizeHandlePlacement,
  resizeHandleContainerRef,
  resizeHandleTooltipDefaultIndex,
  getResizeHandleTooltip,
  useTechnicalDates,
  isLoading,
  renderTooltipFooter,
  highlightedRunningTimeId
}) => {
  const {t} = useTranslation(RUNNING_TIMES_WIDGET_NAMESPACE)
  const timezone = contextTimezone || getSystemTimezone()
  const {ref: barRef, width: barWidth} = useResizeObserver()
  const [dragRunningTimeIndex, setDragRunningIndex] = useState<number | undefined>(
    resizeHandleTooltipDefaultIndex
  )
  const [pinnedTooltipIndex, setPinnedTooltipIndex] = useState<number | undefined>(
    resizeHandleTooltipDefaultIndex
  )

  const totalRunningTimeMs = useMemo(() => {
    return timeRange.endDate.diff(timeRange.startDate, 'milliseconds')
  }, [timeRange])

  const {containerRef, handleMouseUp, handleMouseMove, handleMouseDown, dragEndX, dragStartX} =
    useStackedBarDragRange({
      totalRunningTimeMs,
      dragRunningTimeIndex,
      onDragEnd,
      onMove
    })

  const barPercentages = useMemo<string[] | undefined>(() => {
    if (isEditMode) return getBarPercentages(totalRunningTimeMs, data, barWidth, useTechnicalDates)
  }, [barWidth, data, totalRunningTimeMs, useTechnicalDates, isEditMode])

  useEffect(() => {
    onResize?.(data, barWidth)
  }, [barWidth, data, onResize])

  return (
    <Box
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      sx={{
        flex: '1',
        position: 'relative',
        userSelect: 'none'
      }}
      data-test-id="stacked-bar-chart"
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: 'common.white',
            zIndex: 3,
            opacity: 0.4,
            cursor: 'wait'
          }}
        />
      )}
      {data && (
        <Box
          ref={barRef}
          sx={{
            flex: '1',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: customThemeConstants().palette.neutralFaded20
          }}
        >
          {data.map((rt, index) => {
            const rtDto = mapRunningTimeDto(rt)
            const barForChartProps = getBarForChartProps({
              timezone,
              isEditMode,
              barPercentages,
              rt,
              rtDto,
              aboveBarComponent,
              handleRunningClick,
              totalRunningTimeMs,
              sx,
              data,
              index
            })
            const shouldShowResizeHandle =
              isEditMode && index < data.length - 1 && !data[index + 1]?.stoppages?.length

            if (rt.stoppages?.length) {
              return rt.stoppages.map((stoppage, stoppageIndex) => {
                const {from, to} = getStoppageDate(stoppage, rt)

                return (
                  <BarForChart
                    {...barForChartProps}
                    key={`${index}-${stoppageIndex}`}
                    stoppageIndex={stoppageIndex}
                    percentage={
                      barPercentages?.[index] || getBarPercentage(from, to, totalRunningTimeMs)
                    }
                    handleStoppageClick={
                      handleStoppageClick
                        ? (e) => {
                            e.stopPropagation()
                            void handleStoppageClick(rtDto, stoppage)
                          }
                        : undefined
                    }
                    tooltip={{
                      header: !isEditMode && equipment && (
                        <RunningTimeStoppageLabel
                          {...getRunningTimeStoppageLabelProps(
                            stoppageCodesByType,
                            stoppage,
                            getEventStoppageType(equipment)
                          )}
                        />
                      ),
                      body:
                        isEditMode && t('stackedBarChart.message.definedStoppagesCannotBeSplit'),
                      footer: renderTooltipFooter?.(rt)
                    }}
                  />
                )
              })
            }
            return (
              <Fragment key={index}>
                <BarForChart
                  {...barForChartProps}
                  handleStoppageClick={
                    handleStoppageClick
                      ? (e) => {
                          e.stopPropagation()
                          void handleStoppageClick(rtDto)
                        }
                      : undefined
                  }
                  tooltip={{
                    footer: renderTooltipFooter?.(rt)
                  }}
                >
                  {highlightedRunningTimeId === rt.id && (
                    <Box
                      data-test-id={`current-stoppage-indicator-${index}`}
                      sx={{
                        color: 'warning.light',
                        position: 'fixed',
                        fontSize: (theme) => theme.spacing(1),
                        marginTop: (theme) => theme.spacing(-2)
                      }}
                    >
                      ▼
                    </Box>
                  )}
                </BarForChart>

                {shouldShowResizeHandle && (
                  <ResizeHandle
                    isPinned={pinnedTooltipIndex === index}
                    setIsPinned={(isPinned) => setPinnedTooltipIndex(isPinned ? index : undefined)}
                    isTooltipOpen={
                      resizeHandleTooltipDefaultIndex === undefined
                        ? dragStartX === null
                          ? undefined
                          : index === dragRunningTimeIndex
                        : index === dragRunningTimeIndex
                    }
                    tooltipBody={getResizeHandleTooltip?.(rt, data[index + 1])}
                    placement={resizeHandlePlacement}
                    containerRef={resizeHandleContainerRef}
                    handleResizeStart={() => setDragRunningIndex(index)}
                    handleResizeEnd={
                      resizeHandleTooltipDefaultIndex === undefined
                        ? () => setDragRunningIndex(undefined)
                        : undefined
                    }
                    data-test-id={`resize-handle-${index}`}
                  />
                )}
              </Fragment>
            )
          })}
        </Box>
      )}

      {showDraggableArea && dragStartX !== null && dragEndX !== null && (
        <Box
          sx={({palette}) => ({
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: Math.min(dragStartX, dragEndX),
            width: Math.abs(dragEndX - dragStartX),
            backgroundColor: alpha(palette.primary.main, 0.4),
            zIndex: 1
          })}
        />
      )}
    </Box>
  )
}
